import React from 'react';
import styled from 'styled-components';
import { useEditMode } from '../../shared/hooks/useEditMode';
import { useWorkViewStore } from '../../shared/store/WorkViewStore';
import { useWorkListActions } from '../../shared/hooks/useApi/useWorkList';
import { AlertPosition, AlertType, SelectType, TextAlignment, DimensionsType, WorkType, usesWorkDuration, WorkContentMode, SizeUnit } from '../../shared/enum/EnumRepository';
import { convertWorkSizeToString, getMainSizeData, hasSizeDataForUnit, SizeData, WorkData, WorkPropertyVisibleToggleField, Money } from '../../shared/dto/EntityRepository';
import { WorkViewerInfo, WorkViewerTitle } from '../../shared/component/headless/input/InputBody';
import { NormalSmallTextArea, WorkViewerInfoTextArea } from '../../shared/component/headless/textarea/TextAreaBody';
import MoleculeInputDiv from '../../shared/component/molecule/MoleculeInputDiv';
import MoleculeTextAreaDiv from '../../shared/component/molecule/MoleculeTextAreaDiv';
import WorkDetailList from './WorkDetailList';
import { useValidation } from '../../shared/hooks/useValidation';
import { convertS3UrlToCloudFrontUrl } from '../../shared/aws/awsHelper';
import OrgDescription from '../../shared/component/organism/OrgDescription';
import { useWorkPropertyVisible } from '../../shared/hooks/useApi/useWorkPropertyVisible';
import OrgInputDivVisi from '../../shared/component/organism/OrgInputDivVisi';
import OrgSelectDivVisi from '../../shared/component/organism/OrgSelectDivVisi';
import SizeInputWithMainUnit from './SizeInputWithMainUnit';
import SizeInput from './SizeInput';
import DimensionsTypeSelect from './DimensionsTypeSelect';
import PriceInput from './PriceInput';
import MoleculeDivToggle from '../../shared/component/molecule/MoleculeDivToggle';
import RelatedProjectList from './RelatedProjectListInWork';
import WorkViewerImg from './WorkViewerImg';
import WorkViewerVideo from './WorkViewerVideo';
import WorkTagInViewer from './WorkTagInViewer';
import MemberCreditInViewer from './MemberCreditInViewer';
import WorkArtistInViewer from './WorkArtistInViewer';
import LoadingInComp from '../../shared/component/LoadingInComp';
import { getPageMessage } from '../../shared/constants/pageMessages';
import { useWorkTagStore } from '../../shared/store/workTagStore';
import { useWorkContentModeStore } from '../../shared/store/workContentModeStore';
import { useSettingStore } from '../../shared/store/settingStore';
import { getWorkTextFieldValue, isWorkEnglishShown } from '../../shared/util/workDisplayText';
import { resolveWorkArtistName, shouldShowWorkArtist } from '../../shared/util/workArtist';
import { useAuth } from '../../shared/hooks/useApi/useAuth';
import { usePageOwnerIdentityStore } from '../../shared/store/pageOwnerIdentityStore';
import { canShowWorkTagList } from '../../shared/util/visibility/workTagVisibility';
import {
  INPUT_COLLECTION_MIN_LENGTH,
  INPUT_COLLECTION_MAX_LENGTH,
  WORK_DESCRIPTION_MAX_LENGTH,
  DESCRIPTION_MAX_ROWS,
  INPUT_YEAR_MAX_LENGTH,
  INPUT_YEAR_MIN_LENGTH,
  INPUT_RUNNING_TIME_MIN_LENGTH,
  INPUT_RUNNING_TIME_MAX_LENGTH,
  INPUT_SIZE_TEXT_MIN_LENGTH,
  INPUT_SIZE_TEXT_MAX_LENGTH
} from '../../shared/constants/limits';
import { useStandardAlertStore } from '../../shared/store/portal/alertStore';
import { getAlertMessage } from '../../shared/constants/alertMessages';

const WorkViewer: React.FC = () => {
  const { isEditMode } = useEditMode();
  const { toggleWorkAllowDownload, toggleWorkAccess } = useWorkListActions();
  const activeWork = useWorkViewStore((s) => s.activeWork);
  const handleChange = useWorkViewStore((s) => s.updateActiveWork);
  const isOverviewOpen = useWorkViewStore((s) => s.isOverviewOpen);
  const { workPropertyVisible, updateWorkPropertyVisible } = useWorkPropertyVisible();
  const tags = useWorkTagStore((s) => s.tags);
  const listOwnerAui = useWorkTagStore((s) => s.listOwnerAui);
  const tagStatus = useWorkTagStore((s) => s.status);
  const { isOwner } = useAuth();
  const pageOwner = usePageOwnerIdentityStore((s) => s.pageOwner);
  const canShowTags = canShowWorkTagList({
    pageOwnerAui: pageOwner?.aui,
    listOwnerAui,
    status: tagStatus,
  });
  const visibleTags = canShowTags ? tags : [];
  const { checkType } = useValidation();
  const { enqueueAlert } = useStandardAlertStore();
  const workContentMode = useWorkContentModeStore((s) => s.workContentMode);
  const setting = useSettingStore((s) => s.setting);
  const isWorkEng = workContentMode === WorkContentMode.ENG;

  if (!isOverviewOpen || !workPropertyVisible) return null;
  if (!activeWork) return <LoadingInComp isLoading={true} />;

  const engVisible = workPropertyVisible.engVisible !== false;
  const showEngLabel = isWorkEnglishShown(workContentMode, engVisible, isEditMode);
  const titleValue = getWorkTextFieldValue(
    activeWork, 'title', workContentMode, engVisible, isEditMode,
  );
  const materialValue = getWorkTextFieldValue(
    activeWork, 'material', workContentMode, engVisible, isEditMode,
  );
  const descriptionValue = getWorkTextFieldValue(
    activeWork, 'description', workContentMode, engVisible, isEditMode,
  );
  const sizeTextValue = activeWork.sizeText ?? '';
  const resolvedTitle = getWorkTextFieldValue(
    activeWork, 'title', workContentMode, engVisible, false,
  );
  const resolvedMaterial = getWorkTextFieldValue(
    activeWork, 'material', workContentMode, engVisible, false,
  );
  const artistName = shouldShowWorkArtist(setting)
    ? resolveWorkArtistName(activeWork.artist, workContentMode, engVisible)
    : '';

  const mainSize = getMainSizeData(activeWork.sizes, activeWork.mainSizeUnit);
  const convertedStringSize = convertWorkSizeToString(
    mainSize,
    activeWork.dimensionsType,
    sizeTextValue,
  );
  const convertedCloudFrontUrl = convertS3UrlToCloudFrontUrl(activeWork.uploadFile.originUrl);
  const sizeUnits = Object.values(SizeUnit);

  const handleChangeWithValidate = (
    field: keyof WorkData | 'size',
    value: number | string | boolean | Money | SizeData | DimensionsType
  ) => {
    if (field !== 'size' && field !== 'isPOV' && field !== 'price' && field !== 'dimensionsType' && field !== 'videoUrl' && field !== 'duration') {
      if (!checkType(field, value as string)) {
        return;
      };
    }
    handleChange({ [field]: value });
  }

  const handleSizeChange = (unit: SizeUnit, next: SizeData) => {
    const nextSize = { ...next, unit };
    const hasUnitSize = activeWork.sizes.some((size) => size.unit === unit);
    handleChange({
      sizes: hasUnitSize
        ? activeWork.sizes.map((size) => size.unit === unit ? nextSize : size)
        : [nextSize, ...activeWork.sizes],
    });
  };

  const handleMainSizeUnitChange = (unit: SizeUnit) => {
    if (!hasSizeDataForUnit(activeWork.sizes, unit)) {
      enqueueAlert({
        type: AlertType.ALERT,
        position: AlertPosition.TOP,
        content: getAlertMessage('SET_MAIN_SIZE_UNIT_REQUIRES_SIZE'),
      });
      return;
    }
    handleChange({
      mainSizeUnit: unit,
    });
  };

  const handleVisibleChange = async (field: WorkPropertyVisibleToggleField) => {
    if (!activeWork) return;
    if (!isEditMode) return null;
    await updateWorkPropertyVisible({
      ...workPropertyVisible,
      [field]: !workPropertyVisible[field]
    });
  };

  const handleToggleAllowDownload = async (value: boolean) => {
    if (!activeWork) return;
    const { uploadFile, ...workWithoutUploadFile } = activeWork;
    await toggleWorkAllowDownload({
      ...workWithoutUploadFile,
      artist: undefined,
      tagIds: [],
      updateUploadFileReq: {
        originUrl: uploadFile.originUrl,
        uploadFileId: uploadFile.id,
        allowDownload: value,
        width: uploadFile.width,
        height: uploadFile.height,
      }
    });
  };

  const handleIsPublicToggle = async (value: boolean) => {
    if (!activeWork) return;
    if (!isEditMode) return;
    await toggleWorkAccess({
      id: activeWork.id,
      isPublic: value,
    });
  };


  return (
    <>
      <WorkInfoContainer>
        <MoleculeInputDiv
          value={titleValue}
          defaultValue={"Title"}
          DefaultDiv={TitleDefaultDiv}
          placeholder={"Title"}
          handleChange={(e) =>
            handleChangeWithValidate(isWorkEng ? 'titleEn' : 'title', e.target.value)
          }
          inputStyle={WorkViewerTitle}
          StyledDiv={Title}
        />
        <WorkArtistInViewer />
        <WorkInfo>
          <WorkInfoRow>
            <WorkInfoLabel>Year</WorkInfoLabel>
            <MoleculeInputDiv
              value={activeWork.prodYear}
              defaultValue={"Year"}
              DefaultDiv={DefaultDiv}
              placeholder={"Year"}
              handleChange={(e) => handleChangeWithValidate("prodYear", e.target.value)}
              inputStyle={WorkViewerInfoWithLightBorder}
              StyledDiv={Info}
              minLimit={INPUT_YEAR_MIN_LENGTH}
              maxLimit={INPUT_YEAR_MAX_LENGTH}
            />
          </WorkInfoRow>
          <WorkInfoMaterial>
            <WorkInfoLabel>Material {showEngLabel ? '(en)' : ''}</WorkInfoLabel>
            <MoleculeTextAreaDiv
              value={materialValue}
              defaultValue={"Material"}
              DefaultDiv={DefaultDiv}
              placeholder={"Material"}
              handleChange={(e) =>
                handleChangeWithValidate(isWorkEng ? 'materialEn' : 'material', e.target.value)
              }
              textareaStyle={WorkViewerInfoTextAreaWithLightBorder}
              StyledDiv={Info}
            />
          </WorkInfoMaterial>
          {!usesWorkDuration(activeWork.workType) && (
            <WorkInfoSizes>
              <WorkInfoLabel>Size</WorkInfoLabel>
              <SizeContainer>
                {isEditMode ? (
                  <>
                    <DimensionsTypeSelect
                      value={activeWork.dimensionsType}
                      onChange={(next) => handleChangeWithValidate('dimensionsType', next)}
                    />
                    <SizeInputContainer $dimensionsType={activeWork.dimensionsType}>
                    {activeWork.dimensionsType === DimensionsType.FIXED && sizeUnits.map((unit) => {
                      const size = activeWork.sizes.find((item) => item.unit === unit) ?? { width: '', height: '', unit };
                      return (
                        <SizeInputWithMainUnit
                          key={unit}
                          value={size}
                          onChange={(next) => handleSizeChange(unit, next)}
                          StyledDiv={Info}
                          isMainUnit={activeWork.mainSizeUnit === unit}
                          onMainUnitChange={() => handleMainSizeUnitChange(unit)}
                        />
                      );
                    })}
                    {activeWork.dimensionsType === DimensionsType.CUSTOM && (
                      <MoleculeInputDiv
                        value={sizeTextValue}
                        defaultValue={"Custom size"}
                        DefaultDiv={DefaultDiv}
                        placeholder={"Custom size"}
                        handleChange={(e) => handleChangeWithValidate('sizeText', e.target.value)}
                        inputStyle={WorkViewerInfoWithLightBorder}
                        StyledDiv={Info}
                        minLimit={INPUT_SIZE_TEXT_MIN_LENGTH}
                        maxLimit={INPUT_SIZE_TEXT_MAX_LENGTH}
                      />
                    )}
                    </SizeInputContainer>

                  </>
                ) : (
                  <SizeInput
                    value={mainSize}
                    onChange={(next) => handleSizeChange(activeWork.mainSizeUnit, next)}
                    StyledDiv={Info}
                    dimensionsType={activeWork.dimensionsType}
                    displayValue={convertedStringSize}
                  />
                )}
              </SizeContainer>
            </WorkInfoSizes>
          )}
          {usesWorkDuration(activeWork.workType) && (
            <WorkInfoRow>
              <WorkInfoLabel>Duration</WorkInfoLabel>
              <MoleculeInputDiv
                value={activeWork.duration}
                defaultValue={"00:00:00"}
                DefaultDiv={DefaultDiv}
                placeholder={"00:00:00 (hh:mm:ss)"}
                handleChange={(e) => handleChangeWithValidate("duration", e.target.value)}
                inputStyle={WorkViewerInfoWithLightBorder}
                StyledDiv={Info}
                minLimit={INPUT_RUNNING_TIME_MIN_LENGTH}
                maxLimit={INPUT_RUNNING_TIME_MAX_LENGTH}
              />
            </WorkInfoRow>
          )}
          {(workPropertyVisible.workType || isEditMode) && (
            <WorkInfoRow>
              <WorkInfoLabel>Type</WorkInfoLabel>
              <OrgInputDivVisiWrapper>
                <OrgSelectDivVisi
                  value={activeWork.workType || WorkType.NONE}
                  selectType={SelectType.WORK_TYPE}
                  handleChange={(value) => handleChangeWithValidate("workType", value)}
                  StyledDiv={Info}
                  visible={workPropertyVisible.workType}
                  changeVisible={() => handleVisibleChange('workType')}
                />
              </OrgInputDivVisiWrapper>
            </WorkInfoRow>
          )}
          {(isEditMode || (workPropertyVisible.price && (activeWork.isPOV || !!activeWork.price?.amount))) && (
            <WorkInfoRow>
              <WorkInfoLabel>Price</WorkInfoLabel>
              <PriceInput
                value={activeWork.price}
                onChange={(next) => handleChangeWithValidate("price", next)}
                StyledDiv={Info}
                isPOA={activeWork.isPOV}
                onPOAToggle={() => handleChangeWithValidate("isPOV", !activeWork.isPOV)}
                visible={workPropertyVisible.price}
                changeVisible={() => handleVisibleChange('price')}
              />
            </WorkInfoRow>
          )}
          {(isEditMode || (workPropertyVisible.collection && !!activeWork.collection?.trim())) && (
            <WorkInfoRow>
              <WorkInfoLabel>Collection</WorkInfoLabel>
              <OrgInputDivVisiWrapper>
                <OrgInputDivVisi
                  value={activeWork.collection || ''}
                  defaultValue={"Collection"}
                  placeholder={"Collection"}
                  handleChange={(e) => handleChangeWithValidate("collection", e.target.value)}
                  inputStyle={WorkViewerInfoWithLightBorder}
                  StyledDiv={Info}
                  DefaultDiv={DefaultDiv}
                  visible={workPropertyVisible.collection}
                  changeVisible={() => handleVisibleChange('collection')}
                  minLimit={INPUT_COLLECTION_MIN_LENGTH}
                  maxLimit={INPUT_COLLECTION_MAX_LENGTH}
                />
              </OrgInputDivVisiWrapper>
            </WorkInfoRow>
          )}
          {(isEditMode || (activeWork.tags?.length > 0)) && (
          <WorkInfoTags>
              <WorkInfoLabel>Label</WorkInfoLabel>
              <TagContainer>
                {visibleTags.map((tag) => 
                    <WorkTagInViewer key={tag.id} tag={tag} />
                )}
              </TagContainer>
          </WorkInfoTags>
          )}
        </WorkInfo>
        <OrgDescriptionWrapper>
          <OrgDescription
            value={descriptionValue}
            handleChange={(e) =>
              handleChangeWithValidate(isWorkEng ? 'descriptionEn' : 'description', e.target.value)
            }
            alignment={TextAlignment.LEFT}
            StyledTextarea={NormalSmallTextArea}
            StyledDescription={Description}
            visible={workPropertyVisible.description}
            changeVisible={() => handleVisibleChange('description')}
            maxLength={WORK_DESCRIPTION_MAX_LENGTH}
            maxRows={DESCRIPTION_MAX_ROWS}
          />
        </OrgDescriptionWrapper>
        <MemberCreditInViewer />
        {isEditMode && (
          <WorkAccessToggles>
            {isOwner && (
              <MoleculeDivToggle
                value={activeWork.isPublic}
                name={activeWork.isPublic ? 'Public' : 'Private'}
                handleToggle={(e) => handleIsPublicToggle(e.target.checked)}
              />
            )}
            {activeWork.uploadFile.originUrl && !usesWorkDuration(activeWork.workType) &&
              <MoleculeDivToggle
                value={activeWork.uploadFile.allowDownload}
                name={getPageMessage('DOWNLOAD_TOGGLE_LABEL')}
                handleToggle={(e) => handleToggleAllowDownload(e.target.checked)}
              />
            }
          </WorkAccessToggles>
        )}
      </WorkInfoContainer>

      {usesWorkDuration(activeWork.workType) && (
        <WorkViewerVideo
          title={resolvedTitle}
          convertedCloudFrontUrl={convertedCloudFrontUrl}
          handleVideoUrlChange={(value) => handleChangeWithValidate("videoUrl", value)}
        />
      )}
      {!usesWorkDuration(activeWork.workType) && (
        <WorkViewerImg
          artistName={artistName}
          title={resolvedTitle}
          material={resolvedMaterial}
          contertedStringSize={convertedStringSize}
          prodYear={activeWork.prodYear}
          allowDownload={activeWork.uploadFile.allowDownload}
          convertedCloudFrontUrl={convertedCloudFrontUrl}
        />
      )}
      <WorkDetailList />
      <RelatedProjectList />
    </>
  );
}

const WorkInfoContainer = styled.div`
  display: flex;
  flex-direction: column;

  padding: 4px 0;
`

const Title = styled.h2`
  width: 100%;
  height: fit-content;
  min-height: 28px;

  display: flex;

  padding: 4px 0 10px 0;
  color: ${({ theme }) => theme.colors.color_Gray_03};
  ${({ theme }) => theme.typography.Body_03_1};
`;

const TitleDefaultDiv = styled.div`
  width: 100%;

  display: flex;
  align-items: center;

  margin-bottom: 10px;
  color: ${({ theme }) => theme.colors.color_Gray_05} !important;
  ${({ theme }) => theme.typography.Body_03_1};

  background-color: ${({ theme }) => theme.colors.color_Gray_06};
`;


const WorkInfo = styled.div`
  display: flex;
  flex-direction: column;
  padding-right: 2px;
  gap: 6px;
  margin-bottom: 12px;
  color: ${({ theme }) => theme.colors.color_Gray_03};
`;

const WorkViewerInfoWithLightBorder = styled(WorkViewerInfo)`
  border-bottom: 0.5px solid ${({ theme }) => theme.colors.color_Gray_04};
`;

const WorkViewerInfoTextAreaWithLightBorder = styled(WorkViewerInfoTextArea)`
  border-bottom: 0.5px solid ${({ theme }) => theme.colors.color_Gray_04};
`;

const OrgInputDivVisiWrapper = styled.div`
  width: 100%;
  display: flex;
  align-items: center;
`
const WorkInfoSizes = styled.div`
  height: fit-content;
  display: flex;
  align-items: flex-start;
  gap: 8px;
  ${({ theme }) => theme.typography.Body_04};
`;

const WorkInfoMaterial = WorkInfoSizes;

const WorkInfoTags = styled.div`
  height: fit-content;
  display: flex;
  align-items: flex-start;
  gap: 8px;
  ${({ theme }) => theme.typography.Body_04};
`;

const TagContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`

const Info = styled.div`
  flex: 1 1 auto;            /* 남은 공간 채움 */
  min-width: 0;              /* flex item 줄바꿈 허용 */
  text-align: left;          /* 읽기 쉽게 왼쪽 정렬 */
  white-space: normal;       /* 기본 줄바꿈 허용 */
  word-break: break-word;    /* 긴 단어 쪼개기 */
  overflow-wrap: anywhere;   /* 아주 긴 연속 문자열 강제 줄바꿈 */
  line-height: 1.2;          /* 여러 줄일 때 겹치지 않게 */
`;

const OrgDescriptionWrapper = styled.div`
  width: 100%;
`;

const DefaultDiv = styled.div`
  width: fit-content;
  color: ${({ theme }) => theme.colors.color_Gray_05} !important;
  ${({ theme }) => theme.typography.Body_04};

  background-color: ${({ theme }) => theme.colors.color_Gray_06};
`;

const Description = styled.div`
  padding: 20px 0 24px 0;
  line-height: 1.2;
  color: ${({ theme }) => theme.colors.color_Gray_03};
  ${({ theme }) => theme.typography.Body_04};
`

const WorkInfoRow = styled.div`
  height: 18px;
  min-height: 18px;
  margin-bottom: 1px;
  display: flex;
  align-items: center;
  gap: 8px;
  ${({ theme }) => theme.typography.Body_04};
`;

const WorkInfoLabel = styled.span`
  flex-shrink: 0;
  min-width: 70px;
  padding-top: 2px;
  color: ${({ theme }) => theme.colors.color_Gray_03};
  font-weight: 500;
`;

const WorkAccessToggles = styled.div`
  display: flex;
  flex-direction: column;
  padding: 8px 0 4px;
`;

const SizeContainer = styled.div`
  flex: 1 1 auto;
  min-width: 0;
  display: flex;
  flex-direction: column;
`;

const SizeInputContainer = styled.div<{ $dimensionsType: DimensionsType }>`
  display: flex;
  flex-direction: column;
  gap: 4px;
  height: ${({ $dimensionsType }) => {
    if ($dimensionsType === DimensionsType.FIXED) return '90px';
    if ($dimensionsType === DimensionsType.CUSTOM) return '24px';
    return '0px';
  }};
  margin-top: 4px;
  overflow: hidden;
  transition: height 0.5s ease-in-out;
`;

export default WorkViewer;
